import { useState, useEffect, createContext, useContext, useCallback } from 'react';

// Toast Context
const ToastContext = createContext(null);

// Toast types
const TOAST_TYPES = {
  success: {
    bg: 'bg-green-500',
    icon: '✓',
    borderColor: 'border-green-600'
  },
  error: {
    bg: 'bg-red-500',
    icon: '✕',
    borderColor: 'border-red-600'
  },
  warning: {
    bg: 'bg-yellow-500',
    icon: '⚠',
    borderColor: 'border-yellow-600'
  },
  info: {
    bg: 'bg-blue-500',
    icon: 'ℹ',
    borderColor: 'border-blue-600'
  },
  badge: {
    bg: 'bg-gradient-to-r from-amber-400 to-yellow-500',
    icon: '🏆',
    borderColor: 'border-amber-500'
  }
};

// Individual Toast Component
function Toast({ id, message, type = 'success', emoji, onClose }) {
  const config = TOAST_TYPES[type] || TOAST_TYPES.success;
  const isBadge = type === 'badge';
  
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, isBadge ? 5000 : 3000); // Badge toasts stay longer
    return () => clearTimeout(timer);
  }, [id, onClose, isBadge]);

  return (
    <div 
      className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border-l-4 ${config.borderColor} ${isBadge ? 'bg-gradient-to-r from-amber-50 to-yellow-50' : 'bg-white'} text-gray-800 animate-slide-in min-w-[280px] max-w-md`}
      role="alert"
    >
      <div className={`w-8 h-8 ${isBadge ? config.bg : config.bg} rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0 ${isBadge ? 'animate-bounce-gentle' : ''}`}>
        {emoji || config.icon}
      </div>
      <div className="flex-1">
        {isBadge && <p className="text-xs text-amber-600 font-semibold uppercase tracking-wide">Badge Earned!</p>}
        <p className={`${isBadge ? 'text-base font-bold text-gray-900' : 'text-sm font-medium'}`}>{message}</p>
      </div>
      <button 
        onClick={() => onClose(id)}
        className="text-gray-400 hover:text-gray-600 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// Toast Container Component
export function ToastContainer() {
  const { toasts, removeToast } = useToast();
  
  if (toasts.length === 0) return null;
  
  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          id={toast.id}
          message={toast.message}
          type={toast.type}
          emoji={toast.emoji}
          onClose={removeToast}
        />
      ))}
      <style jsx global>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
        @keyframes bounce-gentle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        .animate-bounce-gentle {
          animation: bounce-gentle 0.6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

// Toast Provider Component
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  
  const addToast = useCallback((message, type = 'success', emoji = null) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type, emoji }]);
  }, []);
  
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  
  const toast = {
    success: (message) => addToast(message, 'success'),
    error: (message) => addToast(message, 'error'),
    warning: (message) => addToast(message, 'warning'),
    info: (message) => addToast(message, 'info'),
    badge: (message, emoji) => addToast(message, 'badge', emoji),
  };
  
  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, toast }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
}

// Hook to use toast
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// Export for direct usage
export default { ToastProvider, useToast, ToastContainer };
